<script setup>
import { ref, onMounted, computed } from 'vue'
import PrimaryButton from '@/Shared/Components/Buttons/PrimaryButton.vue'
import SecondaryButton from '@/Shared/Components/Buttons/SecondaryButton.vue'
import AutoComplete from '@/Public/Partials/ExploreBusOption/AutoComplete.vue'
import { usePositionStore } from '@/Public/Stores/PositionStore'
import { MagnifyingGlassIcon } from '@heroicons/vue/24/outline'
import { AdjustmentsVerticalIcon } from '@heroicons/vue/24/outline'
import LinkButton from '@/Shared/Components/Buttons/LinkButton.vue'
import CheckboxList from '@/App/Pages/Profile/Partials/CheckboxList.vue'
import { useUserSettingsStore } from '@/Public/Stores/userSettingsStore'
import { ChevronRightIcon } from '@heroicons/vue/20/solid'
import { TransitionRoot, Dialog, TransitionChild, DialogPanel } from '@headlessui/vue'

let newPositionStore = usePositionStore()
let newUserSettingsStore = useUserSettingsStore()

const props = defineProps({
  canSearchOnMount: Boolean,
})

const form = ref({ processing: false, errors: {} })
const params = new URLSearchParams(document.location.search)
const autocomplete = ref(null)
const showMapFilters = ref(false)
const checked = ref([])
const allChecked = [1]
const checkboxKey = ref(0)

let openMobileFilters = ref(false)

const allMarkerTypes = [
  { 
    id: 1, 
    type: 'bus-stops', 
    title: 'Bus stops', 
    src: '/images/marker_round_icons/bus-stop-round.png',
    role: 'presentation',
    alt: 'Bus stop icon',
  },
  { 
    id: 2, 
    type: 'train', 
    title: 'Train stations', 
    src: '/images/marker_round_icons/train-round.png',
    role: 'presentation',
    alt: 'Train station icon',
  },
  { 
    id: 3, 
    type: 'taxi', 
    title: 'Taxi ranks', 
    src: '/images/marker_round_icons/taxi-round.png',
    role: 'presentation',
    alt: 'Taxi rank icon',
  },
  { 
    id: 4, 
    type: 'ferry', 
    title: 'Ferry ports', 
    src: '/images/marker_round_icons/ferry-round.png',
    role: 'presentation',
    alt: 'Ferry port icon',
  },
  { 
    id: 5, 
    type: 'charge-point', 
    title: 'EV charge points', 
    src: '/images/marker_round_icons/charge-point-round.png',
    role: 'presentation',
    alt: 'EV charge point icon',
  },
  //  { 
    //  id: 6, 
    //  type: 'live-bus', 
    //  title: 'Live buses', 
    //  src: '/images/marker_round_icons/live-bus-round.png',
    // role: 'presentation',
    // alt: '',
  //  },
  //  { 
    //  id: 7, 
    //  type: 'trail-route', 
    //  title: 'Routes', 
    //  src: '/images/marker_round_icons/trail-route-round.png',
    // role: 'presentation',
    // alt: '',
  //  },
  { 
    id: 8, 
    type: 'poi', 
    title: 'Points of interest', 
    src: '/images/marker_round_icons/poi-round.png',
    role: 'presentation',
    alt: 'Point of interest icon',
  },
]

if (params.has('type') && params.has('id')) {
  const values = {
    type: params.get('type'),
    id: params.get('id'),
    label: params.get('label'),
  }

  if (params.get('type') == 'place') {
    values.lat = params.get('lat')
    values.lng = params.get('lng')
  }

  if (params.get('type') == 'poi') {
    values.icon = params.get('icon')
  }

  autocomplete.value = values
}

onMounted(() => {
  if (autocomplete.value && props.canSearchOnMount.value) {
    onSearch()
  }
  checked.value = allChecked
  toggleMapFilters()
})

const onSearch = () => {
  const params = new URLSearchParams(autocomplete.value).toString()

  if (autocomplete.value.type == 'route') {
    window.location.href = `/about-bus-services/explore-bus-options/route/${autocomplete.value.id}?${params}`
  } else if (autocomplete.value.type == 'place') {
    newPositionStore.setPosition({ lat: Number(autocomplete.value.lat), lng: Number(autocomplete.value.lng) })
  } else if (autocomplete.value.type == 'poi') {
    newPositionStore.setPoiPosition({
      lat: Number(autocomplete.value.lat),
      lng: Number(autocomplete.value.lng),
      id: autocomplete.value.id,
    })
  } else if (autocomplete.value.type == 'bus-stop') {
    newPositionStore.setBusStopPosition({
      lat: Number(autocomplete.value.lat),
      lng: Number(autocomplete.value.lng),
      id: autocomplete.value.id,
    })
  } else {
    window.location.href = `/about-bus-services/explore-bus-options/location/?${params}`
  }
}

const resetFilters = () => {
  newUserSettingsStore.clearFilters()
  checked.value = allChecked
  //due to some vue/antlers update constrains we need to 'reset' container the easies way to
  //due so is to change component key
  checkboxKey.value += 1
}

const toggleMapFilters = () => {
  showMapFilters.value = !showMapFilters.value
}

const updateSearchFilters = (filters) => {
  const selectedTypes = filters.value.map((id) => {
    const markerType = allMarkerTypes.find((marker) => marker.id === id)
    return markerType ? markerType.type : null
  })
  newUserSettingsStore.setMapSearchFilter(selectedTypes)
  checked.value = filters
}

//watchers

//computed
const markerTypes = computed(() => {
  const available = newUserSettingsStore.availableMarkers
  if (!available) return [] // Wait until availableMarkers is populated

  return allMarkerTypes.filter((marker) => available[marker.type] > 0)
})
</script>

<template>
  <div class="md:flex py-4">
    <div class="flex md:w-3/4">
      <div class="flex items-center grow mr-4">
        <div class="relative w-full">
          <AutoComplete v-model="autocomplete" />
        </div>
      </div>
      <div class="flex">
        <PrimaryButton
          class="hidden md:block w-full px-12 justify-center"
          :disabled="!autocomplete"
          @click="onSearch"
        >
          Show me
        </PrimaryButton>
        <div class="flex md:hidden">
          <PrimaryButton
            icon-button
            :class="{ 'opacity-25': form.processing }"
            @click="onSearch"
          >
            <MagnifyingGlassIcon class="w-5 h-5" />
          </PrimaryButton>
          <SecondaryButton
            icon-button
            class="ml-2 bg-white"
            @click="openMobileFilters = true"
          >
            <AdjustmentsVerticalIcon class="w-5 h-5" />
          </SecondaryButton>
        </div>
      </div>
    </div>
    <div class="hidden md:flex items-center justify-between pl-10">
      <div :class="showMapFilters ? '' : 'invisible'">
        <LinkButton
          class="cursor-pointer"
          @click="resetFilters"
        >
          Reset filters
        </LinkButton>
      </div>
      <div class="ml-12">
        <LinkButton
          class="cursor-pointer ml-2"
          @click="toggleMapFilters"
        >
          {{ showMapFilters ? 'Hide map filters' : 'Show map filters' }}
        </LinkButton>
      </div>
    </div>
  </div>

  <div class="hidden md:block">
    <div
      v-if="showMapFilters"
      class="border-t-2"
    >
      <p class="mt-3 md:mb-3 font-bold">Show</p>
      <div>
        <div class="flex-row space-x-4">
          <CheckboxList
            :key="checkboxKey"
            :all-in-one-row="false"
            :option-list="markerTypes"
            :checked="checked"
            @checked-values-changed="updateSearchFilters"
          />
        </div>
      </div>
    </div>
  </div>

  <TransitionRoot
    as="template"
    :show="openMobileFilters"
    @close="openMobileFilters = false"
  >
    <Dialog
      as="div"
      class="relative z-40 md:hidden"
      @close="openMobileFilters = false"
    >
      <!-- Background Overlay -->
      <TransitionChild
        as="template"
        enter="transition-opacity ease-linear duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="transition-opacity ease-linear duration-300"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black bg-opacity-25" />
      </TransitionChild>

      <!-- Modal Panel -->
      <div class="fixed inset-0 z-40 flex justify-end">
        <TransitionChild
          as="template"
          enter="transition ease-in-out duration-300 transform"
          enter-from="translate-x-full"
          enter-to="translate-x-0"
          leave="transition ease-in-out duration-300 transform"
          leave-from="translate-x-0"
          leave-to="translate-x-full"
        >
          <DialogPanel class="w-3/4 max-w-md flex flex-col bg-white shadow-xl overflow-y-auto">
            <!-- Modal Header -->
            <div class="flex items-center justify-end px-4 pt-4 pb-2">
              <LinkButton
                class="flex items-center space-x-2 mt-4"
                @click="openMobileFilters = false"
              >
                <span>Back to home</span>
                <ChevronRightIcon class="w-6 h-6" />
              </LinkButton>
            </div>

            <!-- Modal Content -->
            <div class="p-4">
              <div class="flex items-center justify-between mt-5">
                <!-- Heading -->
                <p class="font-bold text-xl">Map filters</p>

                <!-- Link -->
                <LinkButton @click="resetFilters">Reset filters</LinkButton>
              </div>

              <CheckboxList
                :key="checkboxKey"
                class="mt-5"
                :all-in-one-row="false"
                :option-list="markerTypes"
                :checked="checked"
                @checked-values-changed="updateSearchFilters"
              />
            </div>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
